import type { Order, OrderItem } from './database.types';
import { formatPrice } from './cart';
import { statusLabel } from './orders';

/**
 * Ticket cuisine (parité legacy A1 — impression / auto-print).
 * Logique PURE et testable : construit un modèle de ticket à partir d'une commande
 * + ses lignes, puis un HTML imprimable. L'I/O d'impression (window) est isolée
 * dans `printKitchenTicket`.
 */

export interface TicketLine {
  name: string;
  qty: number;
  /** Options groupées (parité legacy) : ['Viande: Cordon Bleu, Nuggets', 'Sauce: Mayonnaise']. */
  options?: string[];
}

/** Retire le résumé d'options entre parenthèses en fin de nom (quand on a le détail groupé). */
export function cleanProductName(name: string): string {
  return name.replace(/\s*\([^()]*\)\s*$/, '').trim() || name;
}

export interface TicketModel {
  tenantName: string;
  orderNumber: string;
  mode: string; // Retrait / Livraison
  point: string | null; // point de commande (kiosque) si présent
  customer: string | null;
  pickupTime: string | null;
  status: string;
  lines: TicketLine[];
  note: string | null;
  totalText: string;
  printedAt: string;
}

/** Heure « HH:MM » à partir d'une colonne pickup_time (`HH:MM:SS`) — tolérant. */
export function shortTime(pickupTime: string | null | undefined): string | null {
  if (!pickupTime) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(pickupTime);
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : null;
}

/** Construit le modèle de ticket cuisine (pur). */
export function buildTicketModel(
  order: Order,
  items: OrderItem[],
  opts: { tenantName?: string; currency?: 'EUR' | 'CHF'; orderPoint?: string | null } = {},
): TicketModel {
  const currency = opts.currency ?? 'EUR';
  const point =
    opts.orderPoint ??
    (order as { order_point?: string | null }).order_point ??
    null;
  return {
    tenantName: opts.tenantName ?? '',
    orderNumber: order.order_number ?? order.id.slice(0, 6),
    mode: order.pickup_mode === 'delivery' ? 'Livraison' : 'Retrait',
    point: point && point.trim() ? point.trim() : null,
    customer: order.customer_name ?? null,
    pickupTime: shortTime(order.pickup_time),
    status: statusLabel(order.status),
    lines: items.map((it) => {
      const grouped = (it.options ?? []).map((o) => `${o.group}: ${o.values.join(', ')}`);
      return {
        // Si on a les options groupées, on nettoie le résumé entre parenthèses du nom (anti-doublon).
        name: grouped.length ? cleanProductName(it.name) : it.name,
        qty: it.quantity,
        options: grouped.length ? grouped : undefined,
      };
    }),
    note: order.customer_note ?? null,
    totalText: formatPrice(order.total_cents, currency),
    printedAt: new Date().toLocaleString('fr-FR'),
  };
}

/** Échappe le HTML (sécurité d'injection dans le ticket). */
function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** HTML imprimable d'un ticket cuisine (format 80mm). Pur. */
export function ticketHtml(model: TicketModel): string {
  const lines = model.lines
    .map((l) => {
      const opts = (l.options ?? [])
        .map((o) => `<div class="opt">› ${esc(o)}</div>`)
        .join('');
      return `<div class="it"><span class="q">${l.qty}×</span><span class="n">${esc(l.name)}</span></div>${opts}`;
    })
    .join('');
  const noteBlock = model.note
    ? `<div class="note">📝 ${esc(model.note)}</div>`
    : '';
  const pointBlock = model.point
    ? `<div class="point">📍 ${esc(model.point)}</div>`
    : '';
  const customer = model.customer ? `<div class="cust">${esc(model.customer)}</div>` : '';
  const pickup = model.pickupTime ? `<div class="time">⏰ ${esc(model.pickupTime)}</div>` : '';
  return `<!doctype html><html><head><meta charset="utf-8"><title>Ticket ${esc(
    model.orderNumber,
  )}</title><style>
    @page { size: 80mm auto; margin: 4mm; }
    * { font-family: 'Courier New', monospace; }
    body { width: 72mm; margin: 0; color: #000; }
    .brand { text-align:center; font-weight:bold; font-size:14px; text-transform:uppercase; }
    .num { text-align:center; font-size:22px; font-weight:bold; margin:4px 0; }
    .meta { text-align:center; font-size:12px; }
    .point { text-align:center; font-size:15px; font-weight:bold; border:2px solid #000; padding:2px; margin:4px 0; }
    hr { border:none; border-top:1px dashed #000; margin:6px 0; }
    .it { display:flex; gap:6px; font-size:14px; padding:1px 0; }
    .it .q { font-weight:bold; }
    .opt { font-size:12px; padding:0 0 1px 18px; }
    .note { margin-top:6px; font-size:13px; font-weight:bold; border:1px solid #000; padding:3px; }
    .total { text-align:right; font-weight:bold; font-size:14px; margin-top:6px; }
    .foot { text-align:center; font-size:10px; margin-top:8px; }
  </style></head><body>
    <div class="brand">${esc(model.tenantName)}</div>
    <div class="num">#${esc(model.orderNumber)}</div>
    <div class="meta">${esc(model.mode)} · ${esc(model.status)}</div>
    ${customer}${pickup}${pointBlock}
    <hr/>
    ${lines}
    <hr/>
    ${noteBlock}
    <div class="total">Total : ${esc(model.totalText)}</div>
    <div class="foot">Imprimé ${esc(model.printedAt)} · Lil &amp; Co</div>
  </body></html>`;
}

/**
 * Décide d'imprimer automatiquement. Politique parité : on imprime à l'ARRIVÉE
 * d'une nouvelle commande (statut `recue`). Pur (pas d'I/O).
 */
export function shouldAutoPrint(status: Order['status']): boolean {
  return status === 'recue';
}

/**
 * Impression réelle (I/O isolée). Ouvre une fenêtre, écrit le ticket, imprime.
 * Renvoie false si la fenêtre est bloquée (popup blocker).
 */
export function printKitchenTicket(model: TicketModel): boolean {
  if (typeof window === 'undefined') return false;
  const w = window.open('', '_blank', 'width=320,height=600');
  if (!w) return false;
  w.document.write(ticketHtml(model));
  w.document.close();
  w.focus();
  // Laisser le rendu se faire avant d'imprimer.
  setTimeout(() => {
    w.print();
    w.close();
  }, 250);
  return true;
}
